import { useMemo, useState, useEffect } from "react";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { getDashboard } from "../store/slices/auth";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import UploadRoster from "./UploadRoster";

export const bodyClass =
  "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

const initialForm = {
  circleId: "",
  name: "",
  subDivisionId: "",
  inspectorOfficerId: "",
  phone: "",
  lat: "",
  lng: "",
  active: true,
};

export default function CircleMarkUp({
  circles = [],
  totalCircles = 0,
  statistics = {},
  loading = false,
  error = null,
  detailsLoading = false,
  saving = false,

  search,
  setSearch,
  status,
  setStatus,

  subdivisions = [],
  subdivisionsLoading = false,
  inspectors = [],
  inspectorsLoading = false,

  onGetInspectors,
  onGetCircle,
  onCreateCircle,
  onUpdateCircle,
  onAddCircle,
}) {
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");
  const [localInspectors, setLocalInspectors] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    dashboard,
    dashboardLoading,
    dashboardError
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const handleRosterUpload = async ({
    type,
    csvText,
    headers,
    rows,
    replaceExisting,
    fileName,
  }) => {

    return {
      success: true,
      message: `${rows.length} records processed successfully.`,
    };
  };
  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) return;

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("admin");

    sessionStorage.clear();

    if (window.adminSocket) {
      window.adminSocket.disconnect();
      window.adminSocket = null;
    }

    window.location.replace("/login");
  };

  const isEdit = Boolean(editId);

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };


  const getSubDivisionId = (value) => {
    if (!value) return "";
    return typeof value === "object" ? value._id : value;
  };

  const getInspectorId = (value) => {
    if (!value) return "";
    return typeof value === "object" ? value._id : value;
  };

  const getInspectorName = (circle) => {
    return (
      circle.inspectorOfficerId?.displayName ||
      circle.inspectorOfficerId?.username ||
      circle.inspectorName ||
      "Not Assigned"
    );
  };

  const getSubDivisionName = (circle) => {
    return (
      circle.subDivisionId?.name ||
      circle.subDivisionName ||
      "Not Available"
    );
  };

  // Load inspectors whenever Sub-Division changes
  const loadInspectors = async (subDivisionId) => {
    setLocalInspectors([]);

    if (!subDivisionId || !onGetInspectors) return;

    try {
      const result = await onGetInspectors(subDivisionId);
      setLocalInspectors(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Failed to load inspectors:", error);
      setLocalInspectors([]);
    }
  };

  const handleSubDivisionChange = async (subDivisionId) => {
    setForm((previous) => ({
      ...previous,
      subDivisionId,
      inspectorOfficerId: "",
    }));

    setFormError("");
    await loadInspectors(subDivisionId);
  };

  // Add modal
  const openAddModal = async () => {
    onAddCircle?.();

    setEditId(null);
    setForm(initialForm);
    setFormError("");
    setLocalInspectors([]);
    setShowModal(true);
  };

  // Edit modal
  const openEditModal = async (id) => {
    try {
      setFormError("");
      setEditId(id);
      setShowModal(true);
      setLocalInspectors([]);

      const circle = await onGetCircle(id);

      if (!circle) {
        throw new Error("Circle details not found");
      }

      const subdivisionId = getSubDivisionId(circle.subDivisionId);
      const inspectorId = getInspectorId(circle.inspectorOfficerId);

      setForm({
        circleId: circle.circleId || "",
        name: circle.name || "",
        subDivisionId: subdivisionId,
        inspectorOfficerId: inspectorId,
        phone:
          circle.phone ||
          circle.inspectorOfficerId?.phone ||
          "",
        lat: circle.lat ?? "",
        lng: circle.lng ?? "",
        active: circle.active !== false,
      });

      if (subdivisionId) {
        const result = await onGetInspectors(subdivisionId);

        // Include the currently assigned Inspector if the API
        // excludes assigned Inspectors from the available list.
        let available = Array.isArray(result) ? result : [];

        if (
          inspectorId &&
          !available.some((item) => item._id === inspectorId)
        ) {
          const currentInspector =
            typeof circle.inspectorOfficerId === "object"
              ? circle.inspectorOfficerId
              : null;

          if (currentInspector) {
            available = [...available, currentInspector];
          }
        }

        setLocalInspectors(available);
      }
    } catch (error) {
      console.error("Failed to load Circle:", error);
      setFormError(
        error?.message || "Failed to load Circle details"
      );
    }
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditId(null);
    setForm(initialForm);
    setFormError("");
    setLocalInspectors([]);
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.circleId.trim()) {
      setFormError("Please enter Circle ID.");
      return;
    }

    if (!form.name.trim()) {
      setFormError("Please enter Circle name.");
      return;
    }

    if (!form.subDivisionId) {
      setFormError("Please select a Sub-Division.");
      return;
    }

    if (form.phone.trim() && !/^[0-9]{10}$/.test(form.phone.trim())) {
      setFormError("Please enter a valid 10-digit phone number.");
      return;
    }

    const latitude =
      form.lat === "" ? null : Number(form.lat);

    const longitude =
      form.lng === "" ? null : Number(form.lng);

    if (
      (latitude !== null &&
        (!Number.isFinite(latitude) ||
          latitude < -90 ||
          latitude > 90)) ||
      (longitude !== null &&
        (!Number.isFinite(longitude) ||
          longitude < -180 ||
          longitude > 180))
    ) {
      setFormError("Please enter valid GPS coordinates.");
      return;
    }

    const payload = {
      circleId: form.circleId.trim().toUpperCase(),
      name: form.name.trim(),
      subDivisionId: form.subDivisionId,
      inspectorOfficerId: form.inspectorOfficerId || null,
      phone: form.phone.trim() || null,
      lat: latitude,
      lng: longitude,
      active: form.active,
    };

    try {
      if (isEdit) {
        await onUpdateCircle(editId, payload);
      } else {
        await onCreateCircle(payload);
      }

      closeModal();
    } catch (error) {
      setFormError(
        typeof error === "string"
          ? error
          : error?.message || "Unable to save Circle"
      );
    }
  };

  // Keep Inspector list populated if the selected Sub-Division
  // is already available when opening the modal.
  useEffect(() => {
    if (!showModal || !form.subDivisionId) return;
    if (localInspectors.length > 0) return;
  }, [showModal, form.subDivisionId, localInspectors.length]);

  return (
    <>
      {/* HEADER */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 shadow-md h-16 relative z-30">
        {/* 1. OFFICIAL IDENTITY & BRANDING */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-lg shadow-blue-900/50 text-xl font-black shrink-0 border border-blue-400/30">
            🇮🇳
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-black tracking-tight text-white flex items-center gap-2 uppercase">
                Nellore Police

              </h1>
            </div>

          </div>
        </div>
        {/* 2. ICONS-WISE PORTAL NAVIGATION BAR (Proper Website Style) */}
        <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
          <a href="/admin" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🗺️
            </span>
            <span>
              Live Map
            </span>
          </a>
          <a href="/emergency-sos" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚨
            </span>
            <span>
              Incident Grid
            </span>
          </a>


        </nav>
        {/* 3. ACTIONS, TOOLS DROPDOWN LIST, THEME & USER PROFILE */}
        <div className="flex items-center gap-2">
          {/* Register Officer Primary Quick Action */}

          {/* Upload Primary Quick Action */}
          <button onClick={() => setUploadOpen(true)} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg shadow flex items-center gap-1.5 transition">
            <span>
              📤
            </span>
            {' '}
            <span className="hidden sm:inline">
              Upload
            </span>
            {' '}Roster
          </button>
          {/* Operations & Tools Dropdown (Proper List-Wise View) */}
          <div className="relative inline-block text-left" id="header-tools-dropdown-container">


          </div>

          <ThemeSwitcher className="mr-0.5" labelClass="hidden 2xl:inline" />
          <div className="h-6 w-px bg-slate-800 mx-0.5"></div>
          {/* Authenticated Commander Profile & Logout */}
          <div className="flex items-center gap-2 pl-1">
            <div id="header-commander-insignia" className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shadow border border-yellow-400/40 shrink-0" style={{ background: "#172554", color: "#fef08a" }}>
              🦁
            </div>
            <div className="hidden sm:block text-right">
              <div
                id="header-commander-name"
                className="text-xs font-black text-white leading-tight truncate max-w-[130px]"
              >
                {user?.displayName || user?.name || user?.username || "Admin"}
              </div>

              <div
                id="header-commander-scope"
                className="text-[10px] text-amber-300 font-bold leading-tight truncate max-w-[160px]"
              >
                👑 {user?.designation || user?.role || "Administrator"}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Logout"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-900/40 text-slate-300 hover:text-red-300 border border-slate-700 flex items-center justify-center text-sm transition cursor-pointer"
            >
              🚪
            </button>
          </div>
        </div>
      </header>

      {/* TOP KPI METRIC STRIP (CLICK ANY CARD TO OPEN IN SEPARATE GRID VIEW) */}
      <div className="bg-slate-900/90 border-b border-slate-800/80 px-4 py-2 shrink-0 grid grid-cols-10 gap-2 text-xs">

        {/* Total Sub Divisions */}
        <div
          onClick={() => navigate("/sub-divisions")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-purple-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-purple-400 uppercase font-bold tracking-wider">
              Sub Divisions
            </div>
            <div className="text-base font-black text-purple-300">
              {dashboardLoading ? "..." : dashboard?.totalSubDivisions ?? 0}
            </div>
          </div>
          <span className="text-xl text-purple-400 opacity-50">🏢</span>
        </div>

        {/* Total Circles */}
        <div
          onClick={() => navigate("/circles")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-sky-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-sky-400 uppercase font-bold tracking-wider">
              Total Circles
            </div>
            <div className="text-base font-black text-sky-300">
              {dashboardLoading ? "..." : dashboard?.totalCircles ?? 0}
            </div>
          </div>
          <span className="text-xl text-sky-400 opacity-50">⭕</span>
        </div>

        {/* Total Police Stations */}
        <div
          onClick={() => navigate("/stations")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-teal-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-teal-400 uppercase font-bold tracking-wider">
              Police Stations
            </div>
            <div className="text-base font-black text-teal-300">
              {dashboardLoading ? "..." : dashboard?.totalStations ?? 0}
            </div>
          </div>
          <span className="text-xl text-teal-400 opacity-50">🚔</span>
        </div>
        <div onClick={() => navigate("/booths")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-blue-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              Total Booths
            </div>
            <div id="stat-total-booths" className="text-base font-black text-white">
              {dashboardLoading ? "..." : dashboard?.totalPollingStations ?? 0}
            </div>
          </div>
          <span className="text-xl opacity-40">
            🗳️
          </span>
        </div>
        <div onClick={() => navigate("/officers")} className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-indigo-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-indigo-400 uppercase font-bold tracking-wider">
              Total Men
            </div>
            <div id="stat-total-staff" className="text-base font-black text-indigo-300">
              {dashboardLoading ? "..." : dashboard?.totalOfficers ?? 0}
            </div>
          </div>
          <span className="text-xl text-indigo-400 opacity-50">
            👮
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-rose-950/40 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-600/40 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Hyper Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-400">
            🔥
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-amber-950/30 hover:bg-amber-900/40 rounded-lg p-2 border border-amber-600/40 hover:border-amber-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-amber-400 uppercase font-bold tracking-wider">
              Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-amber-400">
            ⚠️
          </span>
        </div>

        <div onClick={() => navigate("/officers")} title="View Verified On-Site Personnel in Grid View" className="cursor-pointer bg-emerald-950/20 hover:bg-emerald-900/30 rounded-lg p-2 border border-emerald-600/30 hover:border-emerald-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">
              On-Site (≤75m)
            </div>
            <div id="stat-onsite" className="text-base font-black text-emerald-400">
              {dashboardLoading ? "..." : dashboard?.atPollingStation ?? 0}
            </div>
          </div>
          <span className="text-xl text-emerald-500 opacity-60">
            ✓
          </span>
        </div>
        <div id="card-kpi-absent" onClick={() => navigate("/officers")} className="cursor-pointer bg-rose-950/30 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-500/50 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Not Attended
            </div>
            <div id="stat-non-attended" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.absentAttendance ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-500">
            ✕
          </span>
        </div>
        <div id="card-kpi-sos" onClick={() => navigate("/emergency-sos")} title="Click to View Active Emergency SOS Distress Incidents Directly" className="cursor-pointer bg-red-950/30 hover:bg-red-900/50 rounded-lg p-2 border border-red-500/50 hover:border-red-400 transition flex items-center justify-between animate-sos-strobe">
          <div>
            <div className="text-[10px] text-red-400 uppercase font-bold tracking-wider">
              Emergency SOS
            </div>
            <div id="stat-sos" className="text-base font-black text-red-500">
              {dashboardLoading ? "..." : dashboard?.activeSOS ?? 0}
            </div>
          </div>
          <span className="text-xl text-red-500">
            🚨
          </span>
        </div>
      </div >

      {/* PAGE TITLE */}
      <section className="px-5 py-5 border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-1">
              District Administration / Circle Management
            </div>

            <h2 className="text-xl font-black text-white">
              🏛️ All Circles
            </h2>

            <p className="text-xs text-slate-400 mt-1">
              View and manage all registered police circles.
            </p>
          </div>

          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
          >
            <i className="fa-solid fa-plus" />
            Add Circle
          </button>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="px-5 py-4  border-slate-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Total Circles"
            value={statistics.total || 0}
            color="text-white"
            bg="bg-slate-900 border-slate-800"
          />

          <StatCard
            label="Active"
            value={statistics.active || 0}
            color="text-emerald-400"
            bg="bg-emerald-950/30 border-emerald-700/40"
          />

          <StatCard
            label="Inactive"
            value={statistics.inactive || 0}
            color="text-rose-400"
            bg="bg-rose-950/30 border-rose-700/40"
          />

          <StatCard
            label="Inspector Assigned"
            value={statistics.withInspector || 0}
            color="text-indigo-400"
            bg="bg-indigo-950/30 border-indigo-700/40"
          />
        </div>
      </section>

      {/* FILTERS */}
      <section className="px-5 py-4  border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-2.5 text-slate-500">
              🔍
            </span>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Circle name, ID, Sub-Division, Inspector or phone..."
              className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatus("ALL");
            }}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs font-bold text-slate-300"
          >
            Reset Filters
          </button>
        </div>

        <div className="max-w-7xl mx-auto mt-3 text-[10px] text-slate-500">
          Showing{" "}
          <span className="text-white font-bold">
            {circles.length}
          </span>{" "}
          filtered result(s), {totalCircles} total circles
        </div>
      </section>

      {/* CIRCLE LIST */}
      <main className="flex-1 p-5 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
              Loading circles...
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-950/20 border border-red-800/40 rounded-2xl">
              <p className="text-red-400 text-sm font-bold">
                Failed to load circles
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {typeof error === "string"
                  ? error
                  : error?.message || "Something went wrong"}
              </p>
            </div>
          ) : circles.length === 0 ? (
            <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-sm font-bold text-white">
                No Circles Found
              </h3>
              <p className="text-xs text-slate-500 mt-2">
                Try changing your search or filters.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {circles.map((circle) => {
                const assigned =
                  Boolean(circle.inspectorOfficerId) ||
                  Boolean(circle.inspectorName);

                return (
                  <div
                    key={circle._id}
                    className="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-blue-500/60 transition shadow-lg"
                  >
                    {/* CARD HEADER */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-black text-white">
                          {circle.name || "Unnamed Circle"}
                        </h3>

                        <p className="text-[11px] text-blue-400 font-bold mt-1">
                          {circle.circleId || "--"}
                        </p>
                      </div>

                      <span
                        className={`text-[9px] px-2 py-1 rounded-lg border font-black ${circle.active
                          ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                          : "bg-red-950 text-red-400 border-red-800"
                          }`}
                      >
                        {circle.active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>

                    {/* DETAILS */}
                    <div className="mt-4 space-y-3 text-xs">
                      <DetailRow
                        label="Circle ID"
                        value={circle.circleId || "--"}
                      />

                      <DetailRow
                        label="Sub-Division"
                        value={getSubDivisionName(circle)}
                      />

                      <DetailRow
                        label="Inspector"
                        value={getInspectorName(circle)}
                      />

                      <DetailRow
                        label="Phone"
                        value={circle.phone || "--"}
                      />



                    </div>

                    {/* FOOTER */}
                    <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between gap-3">
                      <span
                        className={`text-[10px] font-bold ${assigned
                          ? "text-emerald-400"
                          : "text-amber-400"
                          }`}
                      >
                        {assigned
                          ? "Inspector Assigned"
                          : "Inspector Pending"}
                      </span>

                      <button
                        type="button"
                        onClick={() => openEditModal(circle._id)}
                        className="px-3 py-2 rounded-lg bg-blue-600/15 border border-blue-500/30 text-blue-400 hover:bg-blue-600 hover:text-white text-xs font-bold transition"
                      >
                        <i className="fa-solid fa-pen-to-square mr-1" />
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 overflow-y-auto">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl my-auto">
            {/* MODAL HEADER */}
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-white">
                  {isEdit ? "Edit Circle" : "Add New Circle"}
                </h3>
                <p className="text-[11px] text-slate-400 mt-1">
                  Enter Circle information and Inspector assignment.
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="w-8 h-8 rounded-lg bg-slate-800 text-slate-300 hover:bg-red-600 hover:text-white"
              >
                ✕
              </button>
            </div>

            {detailsLoading ? (
              <div className="py-16 text-center text-slate-400 text-sm">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                Loading Circle details...
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* CIRCLE ID */}
                  <FormField label="Circle ID" required>
                    <input
                      type="text"
                      value={form.circleId}
                      onChange={(e) =>
                        updateField(
                          "circleId",
                          e.target.value.toUpperCase()
                        )
                      }
                      placeholder="Enter Circle ID (e.g. CIR001)"
                      maxLength={20}
                      required
                      className={inputClass}
                    />
                  </FormField>

                  {/* CIRCLE NAME */}
                  <FormField label="Circle Name" required>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) =>
                        updateField("name", e.target.value)
                      }
                      placeholder="Enter Circle name"
                      required
                      className={inputClass}
                    />
                  </FormField>

                  {/* SUBDIVISION */}
                  <FormField label="Sub-Division" required>
                    <select
                      value={form.subDivisionId}
                      onChange={(e) =>
                        handleSubDivisionChange(e.target.value)
                      }
                      required
                      disabled={subdivisionsLoading}
                      className={inputClass}
                    >
                      <option value="">
                        {subdivisionsLoading
                          ? "Loading Sub-Divisions..."
                          : "Select Sub-Division"}
                      </option>

                      {subdivisions.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.subDivisionId} - {item.name}
                        </option>
                      ))}
                    </select>
                  </FormField>

                  {/* INSPECTOR */}
                  <FormField label="Assign Inspector">
                    <select
                      value={form.inspectorOfficerId}
                      onChange={(e) =>
                        updateField(
                          "inspectorOfficerId",
                          e.target.value
                        )
                      }
                      disabled={
                        !form.subDivisionId || inspectorsLoading
                      }
                      className={inputClass}
                    >
                      <option value="">
                        {!form.subDivisionId
                          ? "Select Sub-Division first"
                          : inspectorsLoading
                            ? "Loading Inspectors..."
                            : "Select Inspector (Optional)"}
                      </option>

                      {localInspectors.map((inspector) => (
                        <option
                          key={inspector._id}
                          value={inspector._id}
                        >
                          {inspector.displayName ||
                            inspector.username}{" "}
                          ({inspector.officerId || "No ID"})
                        </option>
                      ))}
                    </select>

                    <p className="text-[10px] text-slate-500 mt-1">
                      Only Inspectors belonging to the selected
                      Sub-Division are shown.
                    </p>
                  </FormField>

                  {/* PHONE */}
                  <FormField label="Phone Number">
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={form.phone}
                      onChange={(e) =>
                        updateField(
                          "phone",
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                      placeholder="Enter 10-digit phone number"
                      className={inputClass}
                    />
                  </FormField>

                  {/* ACTIVE STATUS */}
                  <div className="md:col-span-2 flex items-center justify-between bg-slate-950 border border-slate-800 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-xs font-bold text-white">
                        Circle Status
                      </p>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Enable or disable this Circle.
                      </p>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-slate-300">
                        {form.active ? "Active" : "Inactive"}
                      </span>

                      <input
                        type="checkbox"
                        checked={form.active}
                        onChange={(e) =>
                          updateField("active", e.target.checked)
                        }
                        className="w-5 h-5 accent-blue-600"
                      />
                    </label>
                  </div>
                </div>

                {/* ERROR */}
                {formError && (
                  <div className="mx-5 mb-4 p-3 rounded-xl bg-red-950/40 border border-red-800/50 text-red-400 text-xs">
                    {formError}
                  </div>
                )}

                {/* ACTIONS */}
                <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-xs font-bold text-white disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : isEdit
                        ? "Update Circle"
                        : "Create Circle"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      <UploadRoster
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultTab="OFFICERS"
        tabs={[
          "OFFICERS",
          "SUBDIVISIONS",
          "CIRCLES",
          "STATIONS",
          "BOOTHS",
        ]}
        onUpload={handleRosterUpload}
        onSuccess={() => {
          console.log("Refresh officer list");
        }}
      />

      {/* FOOTER */}
      <footer className="px-5 py-3 border-t border-slate-800 bg-slate-900 text-center">
        <p className="text-[10px] text-slate-500">
          Nellore Police • Circle Administration
        </p>
      </footer>
    </>
  );
}

const inputClass =
  "w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500 disabled:opacity-50";

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-slate-300 mb-2">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      {children}
    </div>
  );
}

function DetailRow({ label, value, valueClass = "text-slate-200" }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className={`${valueClass} font-semibold text-right break-words`}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, color, bg }) {
  return (
    <div className={`${bg} border rounded-xl p-3`}>
      <p className="text-[10px] uppercase text-slate-400 font-bold">
        {label}
      </p>
      <h3 className={`text-xl font-black mt-1 ${color}`}>
        {value}
      </h3>
    </div>
  );
}